import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Boxes, Loader2, Moon, Sun } from 'lucide-react';
import { fetchBranding, logoUrl } from '@/lib/branding-api';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';

const FormSchema = z.object({
  email: z.string().trim().min(3, 'Podaj e-mail').max(320),
  password: z.string().min(1, 'Podaj hasło').max(512),
});

type FormValues = z.infer<typeof FormSchema>;

export function Login({ authMode, version }: { authMode?: string; version?: string }) {
  // publiczny odczyt — ekran logowania musi znać branding PRZED sesją
  const { data: branding } = useQuery({ queryKey: ['branding'], queryFn: fetchBranding, staleTime: 300_000 });
  const { login } = useAuth();
  const { t } = useI18n();
  const { theme, toggle } = useTheme();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema), defaultValues: { email: '', password: '' } });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.email, values.password);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('login.failed'));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/*
              BRANDING NA EKRANIE LOGOWANIA (PLAN §8a) — dlatego odczyt brandingu jest
              publiczny: tutaj nie ma jeszcze sesji. Gdy klient nie wgrał logo, zostaje nasza
              ikona; nie pokazujemy obrazka zastępczego udającego logo.
            */}
            {branding?.hasLogo ? (
              <img
                src={logoUrl(branding.logoVersion)}
                alt={branding.displayName}
                className="max-h-10 max-w-48 object-contain"
              />
            ) : (
              <>
                <Boxes className="size-6 text-primary" />
                <span className="text-lg font-semibold tracking-tight">{branding?.displayName ?? t('app.name')}</span>
              </>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
            title={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
          >
            {theme === 'dark' ? <Sun /> : <Moon />}
          </Button>
        </div>

        <Card>
          <CardContent className="pt-5">
            <h1 className="mb-1 text-lg font-semibold tracking-tight">{t('login.title')}</h1>
            <p className="mb-5 text-sm text-muted-foreground">{t('login.subtitle')}</p>

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" required>
                  {t('login.email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  aria-invalid={Boolean(errors.email)}
                  {...register('email')}
                />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" required>
                  {t('login.password')}
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={Boolean(errors.password)}
                  {...register('password')}
                />
                {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
              </div>

              {serverError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  <span>{serverError}</span>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="animate-spin" />}
                {t('login.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          {authMode === 'dev' ? t('login.modeDev') : authMode === 'keycloak' ? t('login.modeKeycloak') : ''}
          {version ? ` · v${version}` : ''}
        </p>
      </div>
    </div>
  );
}
